import axios from 'axios';
import { CartItem, ShippingAddress, PaymentMethod } from '../contexts/CartContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('chefmax_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('chefmax_token');
      localStorage.removeItem('chefmax_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface CreateOrderRequest {
  items: {
    productId: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }[];
  subtotal: number;
  taxAmount: number;
  shippingAmount: number;
  discountAmount: number;
  totalAmount: number;
  currency: string;
  shippingAddress: ShippingAddress;
  billingAddress?: ShippingAddress;
  customerInfo: {
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
  };
  paymentMethod?: string;
  notes?: string;
}

export interface Order {
  id: string;
  user_id: string;
  order_number: string;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
  subtotal: number;
  tax_amount: number;
  shipping_amount: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  shipping_address: ShippingAddress;
  billing_address?: ShippingAddress;
  customer_info: {
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
  };
  payment_status: 'pending' | 'processing' | 'paid' | 'failed' | 'refunded' | 'partially_refunded';
  payment_method?: string;
  tracking_number?: string;
  carrier?: string;
  items?: OrderItem[];
  created_at: string;
  updated_at: string;
  shipped_at?: string;
  delivered_at?: string;
  confirmed_at?: string;
  cancelled_at?: string;
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  product_name: string;
  product_sku?: string;
  supplier_product_code: string;
  category: string;
  brand?: string;
  unit_price: number;
  original_price?: number;
  quantity: number;
  line_total: number;
  discount_amount: number;
  tax_amount: number;
  product_specifications: any;
  product_images: string[];
  product_description?: string;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'returned';
  tracking_number?: string;
  shipped_at?: string;
  delivered_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface OrdersResponse {
  success: boolean;
  data: {
    orders: Order[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export interface OrderResponse {
  success: boolean;
  data: {
    order: Order;
  };
}

class OrderService {
  /**
   * Create a new order
   */
  async createOrder(orderData: CreateOrderRequest): Promise<Order> {
    try {
      const response = await api.post<OrderResponse>('/orders', orderData);
      return response.data.data.order;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  /**
   * Get user's orders with pagination
   */
  async getOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<OrdersResponse['data']> {
    try {
      const response = await api.get<OrdersResponse>('/orders', { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  /**
   * Get specific order by ID
   */
  async getOrder(orderId: string): Promise<Order> {
    try {
      const response = await api.get<OrderResponse>(`/orders/${orderId}`);
      return response.data.data.order;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  }

  /**
   * Update order status (admin only)
   */
  async updateOrderStatus(
    orderId: string,
    statusData: {
      status: Order['status'];
      trackingNumber?: string;
      carrier?: string;
      adminNotes?: string;
    }
  ): Promise<void> {
    try {
      await api.put(`/orders/${orderId}/status`, statusData);
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  /**
   * Validate cart items against current inventory
   */
  async validateCartItems(items: CartItem[]): Promise<{
    valid: boolean;
    errors: string[];
    updatedItems: CartItem[];
  }> {
    try {
      // This would call a backend endpoint to validate inventory
      // For now, we'll simulate the validation
      const errors: string[] = [];
      const updatedItems = items.map(item => {
        // Simulate stock validation
        if (!item.inStock) {
          errors.push(`${item.name} is out of stock`);
        }
        if (item.quantity > item.maxQuantity) {
          errors.push(`Only ${item.maxQuantity} units available for ${item.name}`);
        }
        return item;
      });

      return {
        valid: errors.length === 0,
        errors,
        updatedItems
      };
    } catch (error) {
      console.error('Error validating cart items:', error);
      throw error;
    }
  }

  /**
   * Convert cart items to order items format
   */
  convertCartToOrderItems(cartItems: CartItem[]): CreateOrderRequest['items'] {
    return cartItems.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
      unitPrice: item.price,
      lineTotal: item.price * item.quantity
    }));
  }

  /**
   * Calculate order totals
   */
  calculateOrderTotals(
    subtotal: number,
    taxRate: number = 0,
    shippingCost: number = 0,
    discountAmount: number = 0
  ) {
    const taxAmount = subtotal * taxRate;
    const totalAmount = subtotal + taxAmount + shippingCost - discountAmount;

    return {
      subtotal,
      taxAmount,
      shippingAmount: shippingCost,
      discountAmount,
      totalAmount
    };
  }
}

export const orderService = new OrderService();
